import { Fragment } from 'react';
import {
  PaymentDetailsFeesList,
  PaymentDetailsProductsList,
  PaymentDetailsSubtotal,
  PaymentDetailsServicesList,
  PaymentDetailsBundlesList,
  PaymentDetailsMethod,
} from '@/components/elements/PaymentDetails';

import { ListItem } from '@/components/elements/lists';

import DataGrid from '@/components/elements/lists/DataGrid';
import MerchantLogo from '@/components/elements/MerchantLogo/MerchantLogo';
import { PersonAvatar } from '@/components/elements/redesign/Avatar';
import { _t } from '@/locale';
import {
  SelectedPaymentMethod as PaymentMethod,
  CheckoutDetailsGiftcard,
  CheckoutDetailsService,
  CheckoutDetailsBundle,
  CheckoutDetailsProduct,
  CheckoutDetailsFee,
} from '@/types/checkout';

export type CheckoutDetailsProps = {
  logo: string;
  placeName: string;
  dateTime: string;
  performer: string;
  performerAvatar?: string;
  priceToPay: { label: string; price: string };
  information: string;
  paymentMethod?: PaymentMethod;
  services?: CheckoutDetailsService[];
  bundles?: CheckoutDetailsBundle[];
  giftcards?: CheckoutDetailsGiftcard[];
  products?: CheckoutDetailsProduct[];
  fees?: CheckoutDetailsFee[];
  subtotal?: string;
};

export const baseTranslationKey = 'components.modules.checkout.CheckoutDetails' as const;

const CheckoutDetails = ({
  logo,
  placeName,
  dateTime,
  performer,
  performerAvatar,
  priceToPay,
  information,
  paymentMethod,
  services,
  giftcards,
  bundles,
  products,
  fees,
  subtotal,
}: CheckoutDetailsProps) => {
  if (priceToPay.label === _t('from')) {
    priceToPay.label = _t('total');
    priceToPay.price = _t('Fr.') + ' ' + priceToPay.price;
  }

  if (priceToPay.price === _t('freePrice')) {
    priceToPay.price = '0 kr';
  }

  return (
    <div>
      <ListItem
        type="div"
        verticalAlign="top"
        rightSlot={<MerchantLogo alt="" size="md" variant="default" imgUrl={logo} />}
        underline>
        <div className="space-y-sm">
          <p className="text-black-900 line-clamp-[2] overflow-hidden text-ellipsis break-all text-xl">{placeName}</p>
          <div className="mt-3 flex flex-row flex-wrap items-center gap-4">
            <div className="flex items-center">
              {performerAvatar ? (
                <PersonAvatar className="mr-2" alt={performer} size="sm" imgUrl={performerAvatar} />
              ) : null}
              <p className="text-m text-fg_secondary line-clamp-[1] overflow-hidden text-ellipsis break-all">
                {performer}
              </p>
            </div>
            {dateTime && <p className="text-sm_secondary text-m">{dateTime}</p>}
          </div>
        </div>
      </ListItem>
      <div className="px-lg">
        <DataGrid ariaLabel={_t(`${baseTranslationKey}.ariaLabel.title`)}>
          <PaymentDetailsServicesList services={services} baseTranslationKey={baseTranslationKey} />
          <PaymentDetailsProductsList products={products} baseTranslationKey={baseTranslationKey} />
          <PaymentDetailsBundlesList bundles={bundles} baseTranslationKey={baseTranslationKey} />
          {giftcards?.map(({ code, amount, expiryDate, label, onRemove }) => {
            return (
              <Fragment key={code}>
                <DataGrid.Row hasPaddingTop>
                  <DataGrid.Cell title={label} type="default" />
                  <DataGrid.Cell
                    title={amount}
                    align="right"
                    type="default"
                    ariaLabel={_t(`${baseTranslationKey}.ariaLabel.giftcard.discount`)}
                  />
                </DataGrid.Row>
                <DataGrid.Row underline>
                  <DataGrid.Cell
                    title={
                      <span className="gap-sm flex flex-nowrap items-center">
                        <span className="text-nowrap">{code}</span>
                        {onRemove && (
                          <button className="text-fg_negative text-nowrap underline" onClick={() => onRemove(code)}>
                            {_t(`${baseTranslationKey}.giftcard.removeGiftcard`)}
                          </button>
                        )}
                      </span>
                    }
                    type="description"
                    ariaLabel={_t(`${baseTranslationKey}.ariaLabel.giftcard.code`)}
                  />
                  <DataGrid.Cell
                    title={
                      <span className="text-fg_secondary text-m">
                        {expiryDate ? ` ${_t('expires')}: ${expiryDate}` : ''}
                      </span>
                    }
                    type="muted"
                    align="right"
                    ariaLabel={_t(`${baseTranslationKey}.ariaLabel.giftcard.code`)}
                  />
                </DataGrid.Row>
              </Fragment>
            );
          })}
          <PaymentDetailsSubtotal subtotal={subtotal} baseTranslationKey={baseTranslationKey} />
          <PaymentDetailsFeesList fees={fees} baseTranslationKey={baseTranslationKey} />
          <DataGrid.Row hasPaddingTop={!subtotal && !fees?.length}>
            <DataGrid.Cell title={priceToPay.label} type="header" />
            <DataGrid.Cell
              title={priceToPay.price}
              type="header"
              align="right"
              ariaLabel={_t(`${baseTranslationKey}.ariaLabel.subtotal.amount`)}
            />
          </DataGrid.Row>
          <PaymentDetailsMethod
            paymentMethod={paymentMethod}
            information={information}
            baseTranslationKey={baseTranslationKey}
          />
        </DataGrid>
      </div>
    </div>
  );
};

export default CheckoutDetails;
